import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';
import clsx from 'clsx';
import { StyleSize } from '@app/utils/types';

interface Option {
  value: string;
  label: string;
}

interface Props {
  name: string;
  label?: string;
  containerClass?: string;
  isOptional?: boolean;
  placeholder?: string;
  options: Option[];
  onChange: (value: string) => void;
  error?: string;
  inputClassName?: string;
  value: string;
  sizeStyle?: StyleSize;
  disabled?: boolean;
  className?: string;
}

const getStyle = (size: string) => {
  switch (size) {
    case 'sm': {
      return 'text-sm py-2 px-3';
    }
    case 'md': {
      return 'text-normal py-3 px-4';
    }
    case 'lg': {
      return 'text-lg py-4 px-5';
    }
  }
};

export const Select = (props: Props) => {
  const {
    containerClass,
    label,
    name,
    isOptional,
    placeholder,
    options,
    onChange,
    error,
    inputClassName,
    value,
    sizeStyle = 'sm',
    disabled,
  } = props;

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className={`w-[200px] ${containerClass ?? ''}`}>
      {label && (
        <label htmlFor={name} className="font-medium text-sm inline-block mb-1">
          {label}{' '}
          {isOptional && <span className="text-base-coolGray">(Optional)</span>}
        </label>
      )}
      <Listbox value={value} onChange={onChange}>
        <div className="relative w-full">
          <ListboxButton
            disabled={disabled}
            className={clsx(
              `group bg-gray-100 !text-gray-500 rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${inputClassName ?? ''} ${getStyle(sizeStyle)} pr-10 text-left`,
              { 'border border-system-error': !!error },
              { 'cursor-not-allowed opacity-50': disabled }
            )}
          >
            <span className="block truncate">
              {selectedOption?.label || placeholder || 'Select an option'}
            </span>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <ChevronDownIcon className="w-5 h-5" />
            </div>
          </ListboxButton>
          <ListboxOptions className="absolute left-0 right-0 z-[9999] w-full mt-1 rounded-lg shadow-lg bg-white max-h-64 overflow-y-auto border border-gray-200">
            {[{ label: placeholder, value: '' }, ...options].map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt cursor-pointer"
              >
                {option.label}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
      {error && (
        <p role="alert" className="text-system-error text-sm text-left pt-1">
          {error}
        </p>
      )}
    </div>
  );
};
